import React, { useCallback, useEffect, useState, KeyboardEvent } from 'react';
import { gql, useMutation } from '@apollo/client';

import {
    Button,
    ValidatedInput,
    Input,
    LoadingEllipses,
} from '@trqk/components';
import { locale, setInLocalStorage } from '@trqk/utils';
import { validateInputs } from '@trqk/hooks';

import { useEntities, useToast } from '../../../../../../hooks';
import { getTourCardLocalStorageKey } from '../../../../../../utils';
import {
    INSIGHTS_TOUR_CARD_IDS,
    INSIGHTS_TOUR_CARD_DONE,
} from '../../../../../../constants';

const ADD_INVITATION_TO_TRQK_USER = gql`
    mutation addInvitationToTrqkUser($input: AddInvitationToTrqkUserInput!) {
        addInvitationToTrqkUser(input: $input) {
            id
            invitations {
                company
                email
                first_name
                last_name
                id
                referrer
            }
        }
    }
`;

const inputIds = {
    firstName: 'invitationFirstName',
    lastName: 'invitationLastName',
    email: 'invitationEmail',
    company: 'invitationCompany',
};

interface InvitationFormProps {
    currentUser: User;
    disableSubmit: boolean;
}

/**
 * Renders a form for sending an invitation to join Trqk.
 * @param props {InvitationFormProps}
 */
export const InvitationForm = ({
    currentUser,
    disableSubmit,
}: InvitationFormProps): JSX.Element => {
    const toast = useToast();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [didSubmit, setDidSubmit] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const { id, email: usersEmail } = currentUser;
    const { selectedEntity } = useEntities();
    const selectedEntityId = selectedEntity?.id;
    const localStorageKey = getTourCardLocalStorageKey({
        cardId: INSIGHTS_TOUR_CARD_IDS.step6,
        entityId: selectedEntityId ?? '',
        userId: id ?? '',
        key: INSIGHTS_TOUR_CARD_DONE,
    });

    const [addInvitation, { loading }] = useMutation(
        ADD_INVITATION_TO_TRQK_USER,
        {
            onCompleted: data => {
                const resultId = data?.addInvitationToTrqkUser?.id ?? null;

                if (resultId !== null) {
                    // On successful submission, clear out input and validation states
                    setFirstName('');
                    setLastName('');
                    setEmail('');
                    setCompany('');
                    setDidSubmit(false);
                    setValidationOutputs([]);

                    toast.add({
                        kind: 'info-banner',
                        content: locale.enUS.ACCOUNT.TOASTS.INVITE_SUCCEEDED,
                    });

                    setInLocalStorage(localStorageKey, (): boolean => true);
                } else {
                    toast.add({
                        kind: 'warning-banner',
                        content: locale.enUS.ACCOUNT.TOASTS.INVITE_FAILED,
                    });
                }
            },
            onError: () => {
                toast.add({
                    kind: 'warning-banner',
                    content: locale.enUS.ACCOUNT.TOASTS.INVITE_FAILED,
                });
            },
        },
    );

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.firstName,
                    label: 'First name',
                    required: true,
                    type: 'name',
                    value: firstName,
                },
                {
                    inputId: inputIds.lastName,
                    label: 'Last name',
                    required: true,
                    type: 'name',
                    value: lastName,
                },
                {
                    inputId: inputIds.email,
                    label: 'Email',
                    required: true,
                    type: 'emailOtherThanUser',
                    value: email,
                    args: {
                        usersEmail,
                    },
                },
            ]),
        [firstName, lastName, email, usersEmail],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate, email, firstName, lastName]);

    const handleSubmit = () => {
        setDidSubmit(true);

        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            addInvitation({
                variables: {
                    input: {
                        // id is no longer used in the backend; it gets pulled from the idToken
                        // used to make the call.
                        email: email.toLowerCase(),
                        first_name: firstName,
                        last_name: lastName,
                        company: company ?? undefined,
                    },
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    return (
        <div className="p-6 rounded bg-offwhite h-full flex flex-col justify-between">
            <ValidatedInput
                addClasses="mb-3 flex-none"
                inputId={inputIds.firstName}
                icon="name"
                type="name"
                placeholder="First name"
                value={firstName}
                handleChange={setFirstName}
                validationOutputs={validationOutputs}
            />
            <ValidatedInput
                addClasses="my-3 flex-none"
                inputId={inputIds.lastName}
                icon="name"
                type="name"
                placeholder="Last name"
                value={lastName}
                handleChange={setLastName}
                validationOutputs={validationOutputs}
            />
            {/* Company is not required, so it does not use a ValidatedInput */}
            <Input
                addClasses="my-3 flex-none"
                inputId={inputIds.company}
                icon="company"
                type="text"
                placeholder="Company"
                value={company}
                handleChange={setCompany}
            />
            <ValidatedInput
                addClasses="my-3 flex-none"
                inputId={inputIds.email}
                icon="email"
                type="email"
                placeholder="Email"
                value={email}
                handleChange={setEmail}
                handleKeyPress={(e: KeyboardEvent<Element>): void => {
                    if (e.key === 'Enter') {
                        handleSubmit();
                    }
                }}
                validationOutputs={validationOutputs}
            />
            <div className="flex-grow flex items-end">
                <Button
                    color="primary-light-bg"
                    disabled={disableSubmit}
                    addClasses="text-center"
                    handleClick={handleSubmit}
                >
                    {!loading && (
                        <>
                            {
                                locale.enUS.ACCOUNT.SECTIONS.INVITE_OTHERS
                                    .BUTTONS.SEND_INVITE
                            }
                        </>
                    )}
                    {loading && <LoadingEllipses width={40} />}
                </Button>
            </div>
        </div>
    );
};
